import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanySearchRow } from '../../services/service-company-search';
import Field from '../../components/shared/Field/Field';
import Selector, { SelectorType } from '../../components/shared/Selector/Selector';
import { checkOrgNo } from '../../utils/bt-number';
import BtButton from '../bt-button/bt-button';
import CompanySearch from '../app-company-search/CompanySearch';
import { CompanySearchUnavailable } from '../app-company-search/CompanySearchView';
import useCompanySearchAvailability from '../app-company-search/useCompanySearchAvailability';
import styles from './SupplierIdentity.module.scss';

export type IdentityOption = { label: string; value: 'organizationNumber' | 'vatNumber' };

type Props = {
  identityType: IdentityOption;
  identityOptions: IdentityOption[];
  value: string;
  searchable: boolean;
  errorMessage?: string;
  lookingUp?: boolean;
  onLookUp?: (orgNumber: string) => void;
  onIdentityTypeChange: (option: IdentityOption) => void;
  onTypedChange: (value: string) => void;
  onCompanyChange: (company: CompanySearchRow | null) => void;
};

export default function SupplierIdentity({
  identityType,
  identityOptions,
  value,
  searchable,
  errorMessage,
  lookingUp = false,
  onLookUp,
  onIdentityTypeChange,
  onTypedChange,
  onCompanyChange,
}: Props) {
  const [t] = useTranslation('supplierDatabase');
  const [pick, setPick] = useState<CompanySearchRow | null>(null);
  const { down, onUnavailable, typedFieldRef } = useCompanySearchAvailability();
  const areaRef = useRef<HTMLDivElement>(null);
  const focusSelector = useRef(false);

  const byOrgNumber = identityType.value === 'organizationNumber';
  const picked = byOrgNumber && pick?.orgNumber === value ? pick : null;
  const searching = searchable && byOrgNumber && !down && (!value || picked !== null);

  useEffect(() => {
    if (!focusSelector.current) return;
    focusSelector.current = false;
    // The swap between search and typed field replaces the focused selector; inputmode="none" marks it.
    areaRef.current?.querySelector<HTMLElement>('input[inputmode="none"]')?.focus();
  }, [identityType]);

  const changeIdentityType = (option: SelectorType) => {
    const chosen = identityOptions.find(identity => identity.value === option?.value);
    if (!chosen || chosen.value === identityType.value) return;
    focusSelector.current = true;
    onIdentityTypeChange(chosen);
  };

  const changeCompany = (company: CompanySearchRow | null) => {
    setPick(company);
    onCompanyChange(company);
  };

  const lookUpLabel = t('addSupplier.button.creditCheck', 'Slå upp');

  const typedRow = (
    <div className={styles.identityRow}>
      <Field
        ref={typedFieldRef}
        value={value}
        onChange={onTypedChange}
        placeholder={
          byOrgNumber
            ? t('addSupplier.placeholder.organizationNumber', '101010-1010')
            : t('addSupplier.placeholder.vatNumber', 'SE101010101001')
        }
        fieldOptions={identityOptions}
        selectedField={identityType}
        onFieldChange={changeIdentityType}
        className={`identityField ${styles.identityField}`}
        required
        label={t('addSupplier.label.identityNumber', 'Organisationsnummer/VAT-nummer')}
        errorMessage={errorMessage}
      />
      {onLookUp && byOrgNumber && (
        <BtButton
          class="refresh-credibility-data"
          loaderShow={lookingUp}
          color="white"
          disabled={lookingUp || !checkOrgNo(value)}
          onClick={() => !lookingUp && checkOrgNo(value) && onLookUp(value)}
        >
          {lookUpLabel}
        </BtButton>
      )}
    </div>
  );

  const search = (
    <CompanySearch
      value={picked}
      onChange={changeCompany}
      onUnavailable={onUnavailable}
      label={t('addSupplier.label.company', 'Företag')}
      required
      errorMessage={errorMessage}
      selectedAction={
        onLookUp &&
        picked && (
          <BtButton
            color="white"
            size="xxs"
            tabIndex={0}
            loaderShow={lookingUp}
            disabled={lookingUp}
            onClick={() => !lookingUp && onLookUp(picked.orgNumber)}
          >
            {lookUpLabel}
          </BtButton>
        )
      }
      prefix={
        <Selector
          className={styles.identitySelector}
          aria-label={t('addSupplier.label.identityType', 'Identitetstyp')}
          isSearchable={false}
          value={identityType}
          options={identityOptions}
          onChange={changeIdentityType}
        />
      }
    />
  );

  return (
    <div ref={areaRef}>
      {byOrgNumber && down === 'unavailable' && <CompanySearchUnavailable />}
      {searching ? search : typedRow}
    </div>
  );
}
